import moment from "moment"
import { useEffect, useState } from "react"
import { Card } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import API from "../../common/API"
import Pagination from "../core/Pagination"

const PAGE_SIZE = 5

interface NewsArticleItem {
    id: string
    title: string
    url: string
    author: string | null
    category: string | null
    language: string | null
    publishedAt: string | null
    featuredImageUrl: string | null
}

const RecordNews = (props: { id?: string; nombres?: string; apellido1?: string; apellido2?: string }) => {
    const { id, nombres, apellido1, apellido2 } = props
    const { t } = useTranslation()
    const [records, setRecords] = useState<NewsArticleItem[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [currentPage, setCurrentPage] = useState(0)

    const bindRecords = async () => {
        try {
            setIsLoading(true)
            const res = await API.getAction(
                `Busqueda/News?identifier=${id}&nombres=${encodeURIComponent(nombres || "")}&apellido1=${encodeURIComponent(apellido1 || "")}&apellido2=${encodeURIComponent(apellido2 || "")}`
            )
            if (res.data.status === "ok") {
                setRecords(res.data.response || [])
            } else {
                setRecords([])
            }
            setCurrentPage(0)
        } catch (error) {
            console.error("Error fetching news records:", error)
            setRecords([])
            setCurrentPage(0)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        if (apellido1) bindRecords()
    }, [id, nombres, apellido1, apellido2])

    if (isLoading) {
        return (
            <Card className="my-4">
                <Card.Body className="text-center py-4">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </Card.Body>
            </Card>
        )
    }

    if (records.length === 0) return null

    const totalPages = Math.ceil(records.length / PAGE_SIZE)
    const pageRecords = records.slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE)

    return (
        <Card className="my-4">
            <Card.Header className="display-4">
                <i className="fa fa-newspaper me-2" />
                {t("Noticias")}
            </Card.Header>
            <Card.Body className="p-0">
                <div className="list-group list-group-flush">
                    {pageRecords.map((a) => (
                        <a
                            key={a.id}
                            href={a.url}
                            target="_blank"
                            rel="noreferrer"
                            className="list-group-item list-group-item-action d-flex align-items-start gap-3"
                        >
                            {a.featuredImageUrl && (
                                <img
                                    src={a.featuredImageUrl}
                                    alt=""
                                    style={{ width: 96, height: 64, objectFit: "cover", flexShrink: 0 }}
                                    className="rounded"
                                />
                            )}
                            <div className="flex-grow-1">
                                <div className="fw-semibold">{a.title}</div>
                                <small className="text-muted">
                                    {a.author && <>{a.author} · </>}
                                    {a.category && <>{a.category} · </>}
                                    {a.publishedAt && moment(a.publishedAt).format("DD/MM/YYYY")}
                                </small>
                            </div>
                        </a>
                    ))}
                </div>
            </Card.Body>
            {totalPages > 1 && (
                <Card.Footer>
                    <Pagination
                        totalPages={totalPages}
                        currentPage={currentPage}
                        onPageChanged={(page: number) => setCurrentPage(page)}
                        totalRecords={records.length}
                    />
                </Card.Footer>
            )}
        </Card>
    )
}

export default RecordNews
